import { Component, Injector, OnInit } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import { Tier1ArchitectureAuthService } from "@org-common/lib/architecture/tier1-architecture-auth.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { ViewOption } from "app/features/architecture/organisation-map/organisation-explorer/organisation-explorer.component";
import { teamDashboardPageRoute } from "app/platform/team-dashboard/team-dashboard-page/team-dashboard-page.route";
import { BehaviorSubject, combineLatest, EMPTY, from, Observable } from "rxjs";
import { debounceTime, map, startWith, switchMap, tap } from "rxjs/operators";
import { TeamsUiService } from "../teams-ui.service";

@Component({
    selector: "adapt-navigate-teams-page",
    templateUrl: "./navigate-teams-page.component.html",
    standalone: false,
})
export class NavigateTeamsPageComponent extends BaseRoutedComponent implements OnInit {
    public ViewOption = ViewOption;
    public showMyTeams$ = new BehaviorSubject(false);
    public includePrivateTeams$ = new BehaviorSubject(false);
    public hasTier1Read = false;

    private includePrivateTeamsMenuItem: IAdaptMenuItem = {
        text: "Show private teams",
        isChecked: this.includePrivateTeams$.value,
        onIsCheckedChange: (isChecked: boolean) => this.includePrivateTeams$.next(isChecked),
    };
    private showOnlyMyTeamsItem: IAdaptMenuItem = {
        text: "Show only my teams",
        isChecked: this.showMyTeams$.value,
        separator: true,
        onIsCheckedChange: (isChecked: boolean) => this.showMyTeams$.next(isChecked),
    };
    public pageMenuItems: IAdaptMenuItem[] = [{
        icon: MenuComponent.SmallRootMenu.icon,
        items: [
            this.showOnlyMyTeamsItem,
            this.includePrivateTeamsMenuItem,
        ],
    }];

    public canConfigureTeam = false;
    public displayTeams$: Observable<Team[]> = EMPTY; // showing teams when don't have tier1 read
    private myTeams$ = new BehaviorSubject<Team[]>([]);

    public constructor(
        private teamsService: CommonTeamsService,
        private teamsUiService: TeamsUiService,
        private rxjsBreezeService: RxjsBreezeService,
        private authService: AuthorisationService,
        injector: Injector,
    ) {
        super(injector);

        this.includePrivateTeamsMenuItem.isChecked = this.teamsService.isShowingPrivateTeams;
        this.includePrivateTeams$.next(this.includePrivateTeamsMenuItem.isChecked);
        this.includePrivateTeams$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((showPrivateTeams) => this.teamsService.isShowingPrivateTeams = showPrivateTeams);

        this.authService.getHasAccess(CommonTeamsAuthService.ConfigureTeam).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((val) => {
            this.canConfigureTeam = val;
            this.showOnlyMyTeamsItem.separator = val;
        });
    }

    public ngOnInit() {
        this.rxjsBreezeService.entityTypeChanged(Team).pipe(
            debounceTime(100),
            startWith(undefined), // initial get
            switchMap(() => this.teamsService.promiseToGetActiveTeamsForCurrentPerson()),
            this.takeUntilDestroyed(),
        ).subscribe((teams) => this.myTeams$.next(teams));

        this.authService.getHasAccess(Tier1ArchitectureAuthService.ReadTier1).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((hasTier1Read) => {
            this.hasTier1Read = hasTier1Read;
            this.displayTeams$ = from(this.teamsService.promiseToGetAllActiveTeams())
                .pipe(
                    switchMap((teams) => this.filterForMyTeams(teams)),
                    tap((teams) => {
                        if (teams.length === 0) { // showing CTA
                            this.notifyActivated();
                        }
                    }),
                );
        });
    }

    @Autobind
    public async addTeam() {
        const newTeam = await this.teamsUiService.promiseToCreateTeam();
        // newTeam will be undefined if clicking "Cancel" on the creation dialog
        if (newTeam) {
            teamDashboardPageRoute.gotoRoute({ teamId: newTeam.teamId }).subscribe();
        }
    }

    @Autobind
    public filterForMyTeams(allTeams: Team[]) {
        return combineLatest([this.myTeams$, this.showMyTeams$, this.includePrivateTeams$]).pipe(
            map(([myTeams, showMyTeams, includePrivateTeams]) => {
                const teams = allTeams.filter((team) => includePrivateTeams || !team.isPrivate);
                myTeams = myTeams.filter((team) => includePrivateTeams || !team.isPrivate);
                return showMyTeams
                    ? teams.filter((t) => myTeams.includes(t)
                        || myTeams.some((mt) => mt.rootTeam === t))
                    : teams;
            }),
        );
    }
}
